import { useState } from "react";
import { Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataTable } from "@/components/common/DataTable";
import { DemoBadge, PageHeader, Pill } from "@/components/common/States";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useFetch } from "@/lib/hooks";
import { errorMessage, fmtDate, fmtDateTime } from "@/lib/api";
import { OwnerBilling } from "@/components/billing/OwnerBilling";

function OrgDetails() {
  const { api, org, can, reload } = useWorkspace();
  const [form, setForm] = useState({ name: org.name, industry: org.industry || "", country: org.country || "", website: org.website || "", default_currency: org.default_currency, distance_unit: org.distance_unit });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.patch("/organisations/current", form); await reload(); toast.success("Organisation updated"); }
    catch (err) { toast.error(errorMessage(err)); }
    finally { setBusy(false); }
  };
  const ro = !can("manage");
  return (
    <form onSubmit={save} className="panel grid max-w-3xl gap-4 p-6 sm:grid-cols-2" data-testid="org-details-form">
      {org.is_demo && <div className="sm:col-span-2"><DemoBadge /></div>}
      {[["name", "Organisation name"], ["industry", "Industry"], ["country", "Country"], ["website", "Website"], ["default_currency", "Default currency"]].map(([k, l]) => (
        <div key={k} className="space-y-1.5"><Label className="text-xs" htmlFor={`org-set-${k}`}>{l}</Label>
          <Input id={`org-set-${k}`} disabled={ro} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} data-testid={`org-settings-${k}-input`} /></div>
      ))}
      <div className="space-y-1.5"><Label className="text-xs">Distance unit</Label>
        <Select disabled={ro} value={form.distance_unit} onValueChange={(v) => setForm((f) => ({ ...f, distance_unit: v }))}>
          <SelectTrigger data-testid="org-settings-distance-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="mi">Miles</SelectItem><SelectItem value="km">Kilometres</SelectItem></SelectContent>
        </Select></div>
      {!ro && <div className="sm:col-span-2"><Button type="submit" disabled={busy} data-testid="org-settings-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save changes</Button></div>}
    </form>
  );
}

function InviteForm({ roles, onDone }) {
  const { api } = useWorkspace();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("sales_user");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/members", { email, role }); toast.success(data.status === "active" ? "Member added" : "Invitation created — they join when they sign in with this email"); setEmail(""); onDone(); }
    catch (err) { toast.error(errorMessage(err)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-2 border-b p-4 sm:flex-row" data-testid="invite-member-form">
      <Input type="email" required placeholder="colleague@company.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email-input" />
      <Select value={role} onValueChange={setRole}>
        <SelectTrigger className="sm:w-56" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(roles).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
      </Select>
      <Button type="submit" disabled={busy} data-testid="invite-submit-button"><UserPlus className="mr-1.5 h-4 w-4" />Invite</Button>
    </form>
  );
}

function Members() {
  const { api, can, roles, user } = useWorkspace();
  const { data, loading, error, reload } = useFetch("/members");
  const act = (fn) => async () => { try { await fn(); toast.success("Membership updated"); reload(); } catch (e) { toast.error(errorMessage(e)); } };
  const columns = [
    { key: "email", label: "Member", strong: true, render: (r) => <span>{r.name && r.name !== r.email ? <>{r.name} <span className="block text-xs text-muted-foreground">{r.email}</span></> : r.email}</span> },
    { key: "role", label: "Role", render: (r) => can("manage") && r.user_id !== user.user_id ? (
      <Select value={r.role} onValueChange={(v) => act(() => api.patch(`/members/${r.id}`, { role: v }))()}>
        <SelectTrigger className="h-8 w-52" data-testid={`member-role-select-${r.id}`} onClick={(e) => e.stopPropagation()}><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(roles).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
      </Select>) : r.role_label },
    { key: "status", label: "Status", render: (r) => <Pill value={r.status === "active" ? "active" : "medium"}>{r.status}</Pill> },
    { key: "created_at", label: "Added", date: true },
  ];
  return (
    <div className="panel overflow-hidden" data-testid="members-panel">
      {can("manage") && <InviteForm roles={roles} onDone={reload} />}
      <DataTable columns={columns} rows={data} loading={loading} error={error} onRetry={reload} testid="members-table"
        rowActions={can("manage") ? (r) => (r.user_id === user.user_id ? [] : [{ id: "remove", label: "Remove from organisation", danger: true, onClick: act(() => api.delete(`/members/${r.id}`)) }]) : undefined} />
    </div>
  );
}

function AuditLog() {
  const { data, loading, error, reload } = useFetch("/audit-events", { limit: 500 });
  const columns = [
    { key: "created_at", label: "When", render: (r) => <span className="whitespace-nowrap font-mono text-xs">{fmtDateTime(r.created_at)}</span> },
    { key: "actor_email", label: "Actor" }, { key: "action", label: "Action", render: (r) => <Pill tone="teal">{r.action}</Pill> },
    { key: "entity_type", label: "Entity" },
    { key: "details", label: "Details", render: (r) => <span className="line-clamp-1 max-w-md text-xs text-muted-foreground">{r.details?.label || r.details?.email || r.details?.name || (r.details?.changes ? `Changed: ${Object.keys(r.details.changes).join(", ")}` : "")}</span> },
  ];
  return <div className="panel overflow-hidden"><DataTable columns={columns} rows={data} loading={loading} error={error} onRetry={reload} testid="audit-table" /></div>;
}

const PERM_LABELS = { read: "View records", write: "Create & edit records", delete: "Delete records", manage: "Manage members & settings" };
function RoleMatrix() {
  const { me, roles } = useWorkspace();
  return (
    <div className="panel overflow-x-auto p-2" data-testid="role-matrix">
      <table className="w-full text-sm"><thead><tr className="border-b"><th className="eyebrow p-3 text-left">Permission</th>{Object.values(roles).map((l) => <th key={l} className="eyebrow p-3 text-left">{l}</th>)}</tr></thead>
        <tbody>{Object.entries(PERM_LABELS).map(([p, l]) => <tr key={p} className="border-b last:border-0"><td className="p-3">{l}</td>{Object.keys(roles).map((r) => <td key={r} className="p-3">{me.permissions[p].includes(r) ? <span className="text-primary">Yes</span> : <span className="text-muted-foreground">—</span>}</td>)}</tr>)}</tbody>
      </table>
      <p className="p-3 text-xs text-muted-foreground">Platform Administrator is a separate platform-level role that can list organisations but cannot read tenant records without membership.</p>
    </div>
  );
}

function PlatformOrgs() {
  const { data, loading, error, reload } = useFetch("/platform/organisations");
  const columns = [{ key: "name", label: "Organisation", strong: true, render: (r) => <>{r.name} {r.is_demo && <Pill tone="amber">demo</Pill>}</> }, { key: "industry", label: "Industry" }, { key: "members", label: "Members" }, { key: "created_at", label: "Created", render: (r) => fmtDate(r.created_at) }];
  return <div className="panel overflow-hidden"><DataTable columns={columns} rows={data} loading={loading} error={error} onRetry={reload} testid="platform-orgs-table" /></div>;
}

export default function Settings() {
  const { can, user } = useWorkspace();
  return (
    <div data-testid="settings-page">
      <PageHeader eyebrow="Administration" title="Organisation Settings" description="Organisation profile, members, roles and the audit trail of sensitive operations." />
      <Tabs defaultValue="organisation">
        <TabsList className="mb-4 flex h-auto flex-wrap justify-start">
          <TabsTrigger value="organisation" data-testid="settings-tab-organisation">Organisation</TabsTrigger>
          <TabsTrigger value="members" data-testid="settings-tab-members">Members</TabsTrigger>
          <TabsTrigger value="roles" data-testid="settings-tab-roles">Roles</TabsTrigger>
          {can("manage") && <TabsTrigger value="audit" data-testid="settings-tab-audit">Audit log</TabsTrigger>}
          {user?.is_platform_admin && <TabsTrigger value="platform" data-testid="settings-tab-platform">Platform</TabsTrigger>}
          {user?.is_platform_admin && <TabsTrigger value="pricing" data-testid="settings-tab-pricing">Pricing & profit</TabsTrigger>}
        </TabsList>
        <TabsContent value="organisation"><OrgDetails /></TabsContent>
        <TabsContent value="members"><Members /></TabsContent>
        <TabsContent value="roles"><RoleMatrix /></TabsContent>
        {can("manage") && <TabsContent value="audit"><AuditLog /></TabsContent>}
        {user?.is_platform_admin && <TabsContent value="platform"><PlatformOrgs /></TabsContent>}
        {user?.is_platform_admin && <TabsContent value="pricing"><OwnerBilling /></TabsContent>}
      </Tabs>
      {!can("manage") && <p data-testid="settings-readonly-note" className="mt-4 text-xs text-muted-foreground">Only Organisation Administrators can change settings, manage members or view the full audit log.</p>}
    </div>
  );
}
